import { CampaignDatasource } from "@campaign/data/datasources/campaign.datasource";
import type { CampaignCreateError } from "@campaign/domain/errors/campaign-create.error";
import type { CampaignUpdateError } from "@campaign/domain/errors/campaign-update.error";
import type {
    Campaign,
    CampaignCreate,
    CampaignFilter,
    Campaigns,
    CampaignUpdate,
} from "@campaign/domain/models/campaign.model";
import type { CropTypes } from "@campaign/domain/models/crop-type.model";
import type { CampaignRepository } from "@campaign/domain/repositories/campaign.repository";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Pagination } from "@core/domain/models/pagination.model";
import type { Nullable } from "@core/domain/types/nullable.type";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class CampaignImplRepository implements CampaignRepository {
    constructor(
        @inject(CampaignDatasource)
        private readonly campaignDatasource: CampaignDatasource,
    ) {}

    getAll(
        pagination: Pagination,
        campaignFilter?: CampaignFilter,
    ): ResultAsync<Campaigns, FallbackError> {
        return this.campaignDatasource.fetchAll(pagination, campaignFilter);
    }

    fetchById(
        campaignId: string,
    ): ResultAsync<Nullable<Campaign>, FallbackError> {
        return this.campaignDatasource.fetchById(campaignId);
    }

    create(
        campaign: CampaignCreate,
    ): ResultAsync<Campaign, CampaignCreateError> {
        return this.campaignDatasource.create(campaign);
    }

    update(
        campaignId: string,
        campaign: CampaignUpdate,
    ): ResultAsync<Campaign, CampaignUpdateError> {
        return this.campaignDatasource.update(campaignId, campaign);
    }

    delete(campaignId: string): ResultAsync<true, FallbackError> {
        return this.campaignDatasource.delete(campaignId);
    }

    getAllCropTypes(): ResultAsync<CropTypes, FallbackError> {
        return this.campaignDatasource.fetchAllCropTypes();
    }
}
